import { useCallback } from 'react';
import { useStore } from 'react-redux';
import { notifications, runtime } from '../platform';

const prepareNativeClear = async id => {
    if (!runtime.isNative()) return async () => {};
    try { return await notifications.prepareReadClear(id); }
    catch { return async () => {}; }
};

/**
 * Custom hook for synchronizing notification actions across components
 * Provides centralized notification management with event broadcasting
 */
export const useNotificationSync = () => {
    const store = useStore();
    // Broadcast notification updates to all listening components
    const broadcastNotificationUpdate = useCallback((eventType, data) => {
        const event = new CustomEvent('notificationUpdate', {
            detail: { eventType, data }
        });
        window.dispatchEvent(event);
    }, []);

    // Abort synchronously on logout/lock/account changes, including while Axios
    // is waiting for secure storage or refreshing a token. Keep the subscription
    // until completion even if the component that started the action unmounts.
    const updateNotification = useCallback(async (api, path, eventType, notificationId, returnSnapshot = false) => {
        const initiatingAuth = store.getState().auth;
        if (!initiatingAuth.isAuthenticated) return false;
        const controller = new AbortController();
        const unsubscribe = store.subscribe(() => {
            const auth = store.getState().auth;
            if (!auth.isAuthenticated || auth.token !== initiatingAuth.token ||
                auth.user?.user !== initiatingAuth.user?.user) controller.abort();
        });
        try {
            const clear = await prepareNativeClear(notificationId);
            if (controller.signal.aborted) return false;
            const response = await api.put(path, undefined, { signal: controller.signal });
            if (controller.signal.aborted) return false;
            await clear().catch(() => {});
            if (controller.signal.aborted) return false;
            broadcastNotificationUpdate(eventType, returnSnapshot ? { throughId: response.data.throughId }
                : notificationId === undefined ? {} : { notificationId });
            return returnSnapshot ? response.data : true;
        } catch {
            if (!controller.signal.aborted) console.error('Unable to update notification state.');
            return false;
        } finally {
            unsubscribe();
        }
    }, [store, broadcastNotificationUpdate]);

    // Mark notification as read and broadcast the update
    const markAsReadSync = useCallback((notificationId, api) =>
        updateNotification(api, `/api/notifications/${notificationId}/read`, 'markAsRead', notificationId),
    [updateNotification]);

    // Mark all notifications as read and broadcast the update
    const markAllAsReadSync = useCallback(api =>
        updateNotification(api, '/api/notifications/mark-all-read', 'markAllAsRead'),
    [updateNotification]);

    const openInboxSync = useCallback(api =>
        updateNotification(api, '/api/notifications/open', 'markAllAsRead', undefined, true),
    [updateNotification]);

    // Dismiss notification and broadcast the update
    const dismissNotificationSync = useCallback((notificationId, api) =>
        updateNotification(api, `/api/notifications/${notificationId}/dismiss`, 'dismiss', notificationId),
    [updateNotification]);

    // Broadcast new notification arrival
    const broadcastNewNotification = useCallback((notificationData) => {
        broadcastNotificationUpdate('newNotification', notificationData);
    }, [broadcastNotificationUpdate]);

    // Set up listener for notification updates from other components
    const setupNotificationListener = useCallback((callback) => {
        const handleNotificationUpdate = (event) => {
            callback(event.detail);
        };

        window.addEventListener('notificationUpdate', handleNotificationUpdate);
        
        // Return cleanup function
        return () => {
            window.removeEventListener('notificationUpdate', handleNotificationUpdate);
        };
    }, []);

    return {
        markAsReadSync,
        markAllAsReadSync,
        openInboxSync,
        dismissNotificationSync,
        setupNotificationListener,
        broadcastNotificationUpdate,
        broadcastNewNotification
    };
};
